import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  monthGrid, startOfMonth,
  type CalendarEvent, type IsoDate, type Tag, type TaskInstance, type User,
} from '@hestia/core';
import { api, ApiError, setOverlayContext, type TaskList } from './api.js';
import { clearAll } from './offline/store.js';
import { configureSync, drain, watchConnection } from './offline/sync.js';
import { SyncNotice } from './components/SyncNotice.js';
import { HoldToTalk } from './views/voice/HoldToTalk.js';
import { Review } from './views/voice/Review.js';
import type { Proposal } from '@hestia/core';
import { Avatar, Icon, icons, hueVar, Logo } from './ui.js';
import { Login } from './views/Login.js';
import { Today } from './views/Today.js';
import { Calendar } from './views/Calendar.js';
import { Tasks } from './views/Tasks.js';
import { TaskComposer } from './views/TaskComposer.js';
import { SettingsMenu } from './components/SettingsMenu.js';
import { Household } from './views/Household.js';

type Tab = 'today' | 'calendar' | 'tasks';

export function App() {
  const [me, setMe] = useState<User | null>(null);
  const [ready, setReady] = useState(false);
  const [today, setToday] = useState<IsoDate | null>(null);
  const [tab, setTab] = useState<Tab>('today');

  const [users, setUsers] = useState<User[]>([]);
  const [tags, setTags] = useState<Tag[]>([]);
  /** Whose day we are looking at, by username. `null` is the whole household. */
  const [who, setWho] = useState<string | null>(null);
  /** The tag everything is narrowed to, by id. It applies to every view at
   *  once, like `who`, and is always shown while set — see the filter pill. */
  const [tag, setTag] = useState<string | null>(null);
  const [taskList, setTaskList] = useState<TaskList | null>(null);

  const [todayView, setTodayView] = useState<{
    stillOpen: TaskInstance[]; today: TaskInstance[];
    spanning: TaskInstance[]; events: CalendarEvent[];
  }>({ stillOpen: [], today: [], spanning: [], events: [] });
  const [month, setMonth] = useState<IsoDate | null>(null);
  const [selected, setSelected] = useState<IsoDate | null>(null);
  const [monthInstances, setMonthInstances] = useState<TaskInstance[]>([]);
  const [monthEvents, setMonthEvents] = useState<CalendarEvent[]>([]);
  /** calendar id -> hue, so an event's spine matches its calendar. */
  const [calendarHues, setCalendarHues] = useState<Map<string, string>>(new Map());

  const [adding, setAdding] = useState(false);
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [household, setHousehold] = useState(false);

  /** Voice notes. `null` until the app has asked whether this machine can
   *  transcribe at all — the button is not offered on one that cannot. */
  const [canSpeak, setCanSpeak] = useState(false);
  const [heard, setHeard] = useState<{ transcript: string; proposals: Proposal[] } | null>(null);
  const [listening, setListening] = useState(false);

  /** Send a recording off and open the review over whatever comes back. */
  const speak = useCallback(async (r: { blob: Blob }) => {
    setListening(true);
    try {
      setHeard(await api.speak(r.blob));
    } catch {
      setHeard({ transcript: '', proposals: [] });
    } finally {
      setListening(false);
    }
  }, []);

  // React 19 runs effects twice in development; without this the whole boot
  // chain fires twice on every mount.
  const booted = useRef(false);

  const loadCalendarHues = useCallback(async () => {
    try {
      const c = await api.calendar();
      setCalendarHues(new Map(c.calendars.map((x) => [x.id, x.hue])));
    } catch { /* not connected, or not allowed — the app works without it */ }
  }, []);

  const boot = useCallback(async () => {
    try {
      const r = await api.me();
      if (!r.user) { setMe(null); return; }
      setMe(r.user);
      setToday(r.today);
      setMonth(r.today);
      setSelected(r.today);
      setOverlayContext({ meId: r.user.id, today: r.today });
      const [u, t] = await Promise.all([api.users(), api.tags()]);
      setUsers(u);
      setTags(t);
      setOverlayContext({ tags: t });
      // Anything typed on the last visit, before the connection went, goes out now.
      void drain();
      void api.voiceAvailable().then(setCanSpeak).catch(() => setCanSpeak(false));
      void loadCalendarHues();
    } catch (err) {
      if (!(err instanceof ApiError && err.status === 401)) console.error(err);
      setMe(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => {
    // Coming back from the calendar consent screen: drop the marker so a reload
    // does not look like a fresh connection.
    if (new URLSearchParams(window.location.search).has('calendar')) {
      window.history.replaceState(null, '', window.location.pathname);
    }
    if (booted.current) return;
    booted.current = true;
    void boot();
  }, [boot]);

  // The overlay resolves tags and attributes new tasks against these, so they
  // have to follow the app rather than the boot they were first set from.
  useEffect(() => { setOverlayContext({ tags }); }, [tags]);
  useEffect(() => { if (today) setOverlayContext({ today }); }, [today]);

  const assignee = useMemo(
    () => (who ? users.find((u) => u.username === who)?.id : undefined),
    [who, users],
  );

  const refreshToday = useCallback(async () => {
    const r = await api.today(assignee);
    setToday(r.date);
    setTodayView({ stillOpen: r.stillOpen, today: r.today, spanning: r.spanning, events: r.events });
  }, [assignee]);

  /** Fetch every day the grid draws, not just the month — otherwise the leading
   *  and trailing days render as empty when they are not. */
  const refreshMonth = useCallback(async (m: IsoDate) => {
    const grid = monthGrid(m);
    const r = await api.range(grid[0]!, grid[grid.length - 1]!, assignee);
    setMonthInstances(r.instances);
    setMonthEvents(r.events);
  }, [assignee]);

  /** Loaded when the list is being looked at, not on every boot: it is the one
   *  view that reads every task there is. */
  const refreshList = useCallback(async () => {
    setTaskList(await api.taskList(assignee));
  }, [assignee]);

  useEffect(() => { if (me) void refreshToday(); }, [me, refreshToday]);
  useEffect(() => { if (me && tab === 'tasks') void refreshList(); }, [me, tab, refreshList]);
  useEffect(() => { if (me && month) void refreshMonth(month); }, [me, month, refreshMonth]);

  const refreshAll = useCallback(async () => {
    // Tags come back too: adding a task can invent one, and the sidebar has to
    // know about it or the new pill renders as a blank.
    await Promise.all([
      refreshToday(),
      month ? refreshMonth(month) : Promise.resolve(),
      tab === 'tasks' ? refreshList() : Promise.resolve(),
      api.tags().then(setTags),
      loadCalendarHues(),
    ]);
  }, [refreshToday, refreshMonth, refreshList, month, tab, loadCalendarHues]);

  const userMap = useMemo(() => new Map(users.map((u) => [u.id, u])), [users]);
  const tagMap = useMemo(() => new Map(tags.map((t) => [t.id, t])), [tags]);
  /** The sidebar lists tags that something actually uses. A tag with nothing on it
   *  is not a category, it is leftover vocabulary — it stays available in the
   *  create form, where you might want it again, and out of the way here. */
  const usedTags = useMemo(() => tags.filter((t) => t.taskCount > 0), [tags]);

  /** A filter on a tag nothing uses any more would hide everything and offer
   *  nothing to click to undo it, so it lets go on its own. */
  useEffect(() => {
    if (tag && tags.length && !usedTags.some((t) => t.id === tag)) setTag(null);
  }, [tag, tags, usedTags]);
  const activeTag = tag ? tagMap.get(tag) ?? null : null;
  const withTag = useCallback(
    (xs: TaskInstance[]) => (tag ? xs.filter((i) => i.task.tagIds.includes(tag)) : xs), [tag]);
  const shownList = useMemo<TaskList | null>(() => taskList && {
    stillOpen: withTag(taskList.stillOpen), today: withTag(taskList.today),
    upcoming: withTag(taskList.upcoming), repeating: withTag(taskList.repeating),
    done: withTag(taskList.done),
  }, [taskList, withTag]);

  /** Optimistic: the checkbox is the most-tapped control in the app and it has to
   *  feel instant. The refetch afterwards puts the truth back if the write failed. */
  const toggle = useCallback(async (i: TaskInstance) => {
    if (!today) return;
    const next = !i.done;
    const stamp = (x: TaskInstance) =>
      x.task.id === i.task.id && x.originalDate === i.originalDate
        ? { ...x, done: next, stillOpen: !next && x.date < today } : x;
    setTodayView((v) => ({ ...v, stillOpen: v.stillOpen.map(stamp), today: v.today.map(stamp) }));
    setMonthInstances((xs) => xs.map(stamp));
    setTaskList((l) => l && {
      stillOpen: l.stillOpen.map(stamp), today: l.today.map(stamp), upcoming: l.upcoming.map(stamp),
      repeating: l.repeating.map(stamp), done: l.done.map(stamp),
    });
    try {
      await api.complete(i, next);
    } finally {
      void refreshAll();
    }
  }, [today, refreshAll]);

  const moveToToday = useCallback(async (i: TaskInstance) => {
    if (!today) return;
    await api.move(i, today);
    await refreshAll();
  }, [today, refreshAll]);

  /** Leaving the app. Deliberate sign-out wipes the device: the cache holds this
   *  household's tasks and the queue holds writes made as this person, and
   *  neither belongs to whoever opens the app next. An expired session is not
   *  that — it is the same person, about to sign back in — so it keeps both, and
   *  the queue goes out once they do. */
  const leave = useCallback((wipe: boolean) => {
    booted.current = false;
    if (wipe) void clearAll();
    setMe(null);
    setWho(null);
    setTag(null);
    setReady(true);
  }, []);
  const signOut = useCallback(() => leave(true), [leave]);

  // A drained queue means the server now has things the screen was only showing
  // optimistically — refetch so what is drawn is what is actually stored.
  useEffect(() => {
    configureSync({ onDrained: () => { void refreshAll(); }, onUnauthenticated: () => leave(false) });
  }, [refreshAll, leave]);
  useEffect(() => watchConnection(), []);

  if (!ready) return null;
  // Signing in leaves via a full-page redirect to Google and comes back to `/`,
  // so there is no callback to wire here — the reload re-runs boot().
  if (!me || !today || !month || !selected) return <Login />;

  const viewing = who ? users.find((u) => u.username === who) ?? null : null;

  return (
    <div className="app">
      <aside className="sidebar glass">
        <div className="sidebar-scroll">
          <div style={{ display: 'flex', alignItems: 'center', gap: 11, padding: '0 4px 0 10px' }}>
            <Logo size={34} />
            <div className="dsp" style={{ fontSize: 20, flexGrow: 1 }}>Hestia</div>
            <SettingsMenu placement="down" variant="sidebar" />
          </div>

          <nav style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <button className={`nav-item${tab === 'today' ? ' on' : ''}`} onClick={() => setTab('today')}>
              <Icon path={icons.clock} size={19} /><span style={{ flexGrow: 1 }}>Today</span>
            </button>
            <button className={`nav-item${tab === 'calendar' ? ' on' : ''}`} onClick={() => setTab('calendar')}>
              <Icon path={icons.calendar} size={19} /><span style={{ flexGrow: 1 }}>Calendar</span>
            </button>
            <button className={`nav-item${tab === 'tasks' ? ' on' : ''}`} onClick={() => setTab('tasks')}>
              <Icon path={icons.list} size={19} /><span style={{ flexGrow: 1 }}>All tasks</span>
            </button>
          </nav>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div className="sec" style={{ padding: '0 15px' }}>Tags</div>
            {usedTags.length === 0 && (
              <div style={{ padding: '4px 15px', fontSize: 12.5, color: 'var(--ink-3)', lineHeight: 1.5 }}>
                {tags.length
                  ? 'Nothing tagged right now.'
                  : 'None yet — type #something in a task.'}
              </div>
            )}
            {/* Each tag narrows every view to itself; pressing it again lets go.
                These used to be styled exactly like the buttons around them and
                do nothing at all. */}
            {usedTags.map((t) => (
              <button key={t.id} className={`nav-item nav-tag${tag === t.id ? ' on' : ''}`}
                      aria-pressed={tag === t.id}
                      onClick={() => setTag(tag === t.id ? null : t.id)}>
                <span style={{ width: 10, height: 10, borderRadius: 4, background: hueVar(t.hue), flex: 'none' }} />
                <span style={{ flexGrow: 1 }}>{t.name}</span>
              </button>
            ))}
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
            <div className="sec" style={{ padding: '0 15px' }}>Family</div>
            {/* The sidebar list IS the filter — that is why there is no filter row. */}
            <button className={`nav-item${who === null ? ' on' : ''}`} onClick={() => setWho(null)}>
              <Icon path={icons.users} size={24} /><span style={{ flexGrow: 1 }}>Everyone</span>
            </button>
            {users.map((u) => (
              <button key={u.id} className={`nav-item${who === u.username ? ' on' : ''}`}
                      onClick={() => setWho(who === u.username ? null : u.username)}>
                <Avatar user={u} size={24} /><span style={{ flexGrow: 1 }}>{u.displayName}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="sidebar-foot">
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="pill pill-accent" style={{ height: 46, fontSize: 13.5, flexGrow: 1 }}
                    onClick={() => setAdding(true)}>
              <Icon path={icons.plus} size={17} w={2.4} />New task
            </button>
            {/* On a phone this lives above the +; there is no + here, so it
                sits beside the button it belongs with. */}
            {canSpeak && (
              <HoldToTalk variant="inline" disabled={listening} onRecorded={speak} />
            )}
          </div>
          <button className="nav-item" onClick={() => setHousehold(true)}>
            <Avatar user={me} size={24} />
            <span style={{ flexGrow: 1 }}>{me.displayName}</span>
            <Icon path={icons.chevronRight} size={16} />
          </button>
        </div>
      </aside>

      <main className="main">
        <SyncNotice />
        {activeTag && (
          // Always visible while a tag filter is on, in every view: a filter that
          // hides tasks without saying so looks exactly like tasks going missing.
          <div className="filter-pill-row">
            <button className="filter-pill" onClick={() => setTag(null)} aria-label={`Stop filtering by ${activeTag.name}`}>
              <span className="tag-dot" style={{ background: hueVar(activeTag.hue) }} />
              Only {activeTag.name}
              <Icon path={icons.x} size={14} w={2.2} />
            </button>
          </div>
        )}
        {tab === 'tasks' ? (
          <Tasks list={shownList} date={today} me={me} viewing={viewing} tag={activeTag}
                 usedTags={usedTags} users={userMap} tags={tagMap} onTag={setTag}
                 onToggle={toggle} onOpen={(i) => setOpenTaskId(i.task.id)}
                 onMoveToToday={moveToToday} onAdd={() => setAdding(true)} />
        ) : tab === 'today' ? (
          <Today date={today} me={me} viewing={viewing}
                 stillOpen={withTag(todayView.stillOpen)} today={withTag(todayView.today)}
                 spanning={withTag(todayView.spanning)}
                 events={tag ? [] : todayView.events} calendarHues={calendarHues}
                 users={userMap} tags={tagMap}
                 onToggle={toggle} onOpen={(i) => setOpenTaskId(i.task.id)}
                 onMoveToToday={moveToToday} onAdd={() => setAdding(true)} />
        ) : (
          <Calendar month={month} selected={selected} today={today} viewing={viewing}
                    instances={withTag(monthInstances)} events={tag ? [] : monthEvents} calendarHues={calendarHues}
                    users={userMap} tags={tagMap}
                    onMonth={(d) => { setMonth(d); setSelected(startOfMonth(d)); }}
                    onSelect={setSelected}
                    onOpen={(i) => setOpenTaskId(i.task.id)} onToggle={toggle} />
        )}
      </main>

      <div className="fab-stack">
        {canSpeak && (
          <HoldToTalk disabled={listening} onRecorded={speak} />
        )}
        <button className="fab" aria-label="New task" onClick={() => setAdding(true)}>
          <Icon path={icons.plus} size={26} w={2.2} />
        </button>
      </div>
      {listening && (
        <div className="ptt-thinking" role="status" aria-live="polite">Making sense of that…</div>
      )}

      <nav className="tabbar">
        <button className={`tab${tab === 'today' ? ' on' : ''}`} aria-label="Today"
                onClick={() => setTab('today')}><Icon path={icons.clock} size={22} /></button>
        <button className={`tab${tab === 'calendar' ? ' on' : ''}`} aria-label="Calendar"
                onClick={() => setTab('calendar')}><Icon path={icons.calendar} size={22} /></button>
        <button className={`tab${tab === 'tasks' ? ' on' : ''}`} aria-label="All tasks"
                onClick={() => setTab('tasks')}><Icon path={icons.list} size={22} /></button>
        <button className={`tab${household ? ' on' : ''}`} aria-label="Household"
                onClick={() => setHousehold(true)}><Icon path={icons.users} size={22} /></button>
        <SettingsMenu placement="up" variant="tab" />
      </nav>

      {adding && (
        <TaskComposer today={today} users={users} tags={tags}
                      onClose={() => setAdding(false)} onChanged={refreshAll} />
      )}
      {openTaskId && (
        <TaskComposer taskId={openTaskId} today={today} users={users} tags={tags}
                      onClose={() => setOpenTaskId(null)} onChanged={refreshAll} />
      )}
      {heard && (
        <Review transcript={heard.transcript} proposals={heard.proposals} today={today}
                users={users} tags={tags}
                onClose={() => setHeard(null)} onApplied={refreshAll} />
      )}
      {household && (
        <Household users={users} me={me} filter={who} onFilter={setWho}
                   onClose={() => setHousehold(false)} onSignedOut={signOut}
                   onCalendarChanged={refreshAll} />
      )}
    </div>
  );
}
